import localStorage from 'Browser/localStorage';
import {
  Cond,
  RefFormData,
  FormText,
  FormSelect,
  objectToOptionsArray,
} from 'Components/FormComponents';
import debounce from 'Util/debounce';
import formatXml from './formatXml';
import ApiMetadata from 'Api/Metadata';
import RequestEnvelope from './RequestEnvelope';
import RequestAuth from './RequestAuth';
import RequestSerializer from './Serializer';

import LayoutManager from './LayoutManager';

import ace from 'brace';
import 'brace/mode/json';
import 'brace/mode/text';
import 'brace/mode/xml';
import 'brace/ext/searchbox';

// TODO get rid of 'initialized'
var initialized = false;

export default class RequestGenerator {
  constructor(siteConfig, apiConfig) {
    this._siteConfig = siteConfig;
    this._sentTime = null;

    this._formEventHandler = this._formEventHandler.bind(this);
    this._onIncludeClick = this._onIncludeClick.bind(this);
    this._arrayAction = this._arrayAction.bind(this);

    const authMetadata = RequestAuth.getMetadata();

    const form = this._requestForm = new RefFormData();
    this._authForm = new RefFormData();

    const requestGroupOptions = ApiMetadata.getRequestGroupNames()
      .map(_ => ({ value: _, label: _  }));

    const apiVersionOptions = apiConfig.versions
      .map(_ => ({ value: _, label: _  }));

    const requestFormatOptions = objectToOptionsArray({
      json: 'JSON',
      xml: 'XML',
      get: 'GET',
      post: 'POST',
    });

    const outputFormatOptions = objectToOptionsArray({
      json: 'JSON',
      xml: 'XML',
      csv: 'CSV',
    });

    const authTypeOptions = Object.entries(authMetadata.authTypes)
      .map(([ type, opts ]) => ({ value: type, label: opts.label }));

    let formContainer;
    let authParamsContainer;

    this.root = (
      <div class="page-container api-test">
        <div class="panel-top">
          <div class="control-column">
            <div class="form-horizontal" ref={formContainer}>
              <h5>Request</h5>

              <FormSelect
                form={form}
                name="requestGroup"
                label="Request Group"
                options={requestGroupOptions}
                value={siteConfig.DEFAULT_REQUEST_GROUP}
              />
              <FormSelect
                form={form}
                name="requestType"
                label="Request Type"
              />
              <FormText
                form={form}
                name="requestID"
                label="Request ID Tag"
              />

              <h5>Options</h5>

              <FormText
                form={form}
                name="requestURL"
                label="Request URL"
                value={siteConfig.API_ROOT_URL}
              />
              <FormSelect
                form={form}
                name="apiVersion"
                label="API Version"
                options={apiVersionOptions}
                value={siteConfig.DEFAULT_API_VERSION}
              />
              <FormSelect
                form={form}
                name="requestFormat"
                label="Request Format"
                options={requestFormatOptions}
              />
              <FormSelect
                form={form}
                name="outputFormat"
                label="Output Format"
                options={outputFormatOptions}
              />
              <FormText
                form={form}
                name="errorURL"
                label="Error URL"
              />

              <h5>Auth Fields</h5>

              <FormSelect
                form={form}
                name="authType"
                label="Auth Type"
                options={authTypeOptions}
                value={siteConfig.DEFAULT_AUTH_TYPE}
              />

              <div ref={authParamsContainer} />

              <h5>Parameters</h5>

              <div class="dynamic-params" />
            </div>
          </div>

          <div class="output-column">
            <h5>Request <button type="button" class="btn btn-primary" onclick={() => this.sendRequest()}><span class="tbicon-send"></span> Send</button> <small class="destination-url"></small></h5>
            <div id="request-editor"></div>
          </div>
        </div>

        <div class="panel-bottom">
          <h5>Response <span class="requestStatus"></span></h5>

          <div id="response-editor"></div>
        </div>
      </div>
    );

    let authFormData;
    if (siteConfig.SAVE_AUTH_FIELDS) {
      try {
        authFormData = JSON.parse(localStorage.apiAuth);
      } catch (err) {
      }
    }

    Object.entries(authMetadata.authTypes).forEach(([ authType, authTypeOpts ]) => {
      const { params } = authTypeOpts;

      params.forEach(authParamName => {
        const fieldName = `${authType}_${authParamName}`;
        const inputID = this._authForm.getFieldId(fieldName);
        const opts = {
          label: authParamName,
          optional: authMetadata.optionalParams.includes(fieldName),
          form: this._authForm,
          paramValue: authFormData?.[fieldName] ?? '',
        };

        const el = this.addFormInput(true, fieldName, inputID, opts);
        authParamsContainer.appendChild(el);
      });
    });

    this._generateRequestDebounced = debounce(() => {
      this.generateRequest();
    }, 250);

    const bindChange = (field, listener) => this._requestForm.getInput(field).onchange = listener;

    bindChange('authType', () => this.setAuthType());
    bindChange('requestGroup', () => this.setRequestGroup());
    bindChange('requestType', () => this.setRequestType());
    bindChange('requestFormat', () => this.setRequestFormat());
    bindChange('outputFormat', () => this.setOutputFormat());

    formContainer.addEventListener('click', this._formEventHandler);
    formContainer.addEventListener('keyup', this._formEventHandler);
    formContainer.addEventListener('blur', this._formEventHandler);

    formContainer.addEventListener('click', this._arrayAction);
  }

  finishInit() {
    // TODO it looks like we can pass an element to ace instead of an id
    this._requestEditor = ace.edit('request-editor');
    this._responseEditor = ace.edit('response-editor');

    this._requestEditor.$blockScrolling = Infinity;
    this._responseEditor.$blockScrolling = Infinity;

    this._layoutManager = new LayoutManager(this._requestEditor, this._responseEditor, this._siteConfig.MINIMUM_TOP_PANE_HEIGHT);

    this.setAuthType();
    this.setRequestGroup();
  }

  updateTime() {
    var elapsedMS = Date.now() - this._sentTime;

    var elapsedS  = elapsedMS / 1000;

    document.querySelector('.requestStatus').textContent = `Completed in ${elapsedS}s`;
  }

  getEffectiveAuthType(type) {
    if (type === 'accountBridge' || type === 'partnerBridge' || type === 'operatorBridge')
      return 'bridge';

    return type;
  }

  authTypeMatches(authType, authTypeSet) {
    authType = this.getEffectiveAuthType(authType);

    return authTypeSet.indexOf(authType) !== -1;
  }

  setRequestFormat() {
    const requestFormat = this._requestForm.get('requestFormat');
    const outputFormat = (requestFormat === 'get' || requestFormat === 'post') ? 'json' : requestFormat;

    this._requestForm.set('outputFormat', outputFormat);
  }

  setOutputFormat() {
    this.generateRequest();
  }

  setRequestGroup() {
    const group = this._requestForm.get('requestGroup');
    const input = this._requestForm.getInput('requestType');

    // clear requestType <option>s
    input.textContent = '';

    let defaultMethod = null;
    let first = null;
    for (var i in ApiMetadata.getRequestGroupDefinition(group).methods) {
      if (!first) {
        first = i;
      }

      if (group === this._siteConfig.DEFAULT_REQUEST_GROUP && i === this._siteConfig.DEFAULT_REQUEST_TYPE) {
        defaultMethod = i;
      }

      input.appendChild(new Option(i, i));
    }

    this._requestForm.set('requestType', defaultMethod ?? first);
    this.setRequestType();
  }

  setRequestType() {
    const authType = this._requestForm.get('authType');
    const requestGroup = this._requestForm.get('requestGroup');
    const requestType = this._requestForm.get('requestType');

    var oldData = {};
    var oldInclude = {};

    this._getParamInputs().forEach(input => {
      const val = input.value;
      const id = input.id.substr(2);

      if (val !== '') {
        oldData[id] = val;
      }

      oldInclude[id] = this.isParamIncluded(input);
    });

    const insertLocation = document.querySelector('.dynamic-params');
    insertLocation.textContent = '';

    if (requestType) {
      let requestParams = ApiMetadata.getMethodDefinition(requestGroup, requestType).parameters;

      this.addObject(authType, oldInclude, oldData, requestParams, insertLocation);
    }

    initialized = true;

    this.generateRequest();
  }

  addObject(authType, oldInclude, oldData, fields, insertLocation, prefix = '') {
    Object.keys(fields).forEach(fieldName => {
      let cur = fields[fieldName];
      let fullFieldName = `${prefix}${fieldName}`;

      if (cur.disallowedForAuthType) {
        if (this.authTypeMatches(authType, cur.disallowedForAuthType))
          return;
      }

      const topLevel = prefix === '';

      let options = {
        type: cur.type,
        optional: topLevel && !(cur.required || (cur.requiredForAuthType && this.authTypeMatches(authType, cur.requiredForAuthType))),
        include: false,
        values: cur.values,
        nullable: cur.nullable,
        label: fieldName,
      };

      // look and see if this param previously had data and was included
      if (oldInclude[fullFieldName])
        options.include = true;

      if (oldData[fullFieldName] !== undefined)
        options.paramValue = oldData[fullFieldName];

      const el = this.addFormInput(topLevel, fullFieldName, `r_${fullFieldName}`, options);
      insertLocation.appendChild(el);

      if (cur.type === 'object') {
        if (cur.fields) {
          this.addObject(authType, oldInclude, oldData, cur.fields, insertLocation, `${fullFieldName}.`);
        }
      }
    });
  }

  addFormInput(topLevel, fieldName, inputID, options) {
    const { form = null } = options;

    let el;
    const root = (
      <div class="form-group">
        <label class="col-sm-4 control-label" for={inputID}>
          <Cond test={!topLevel}>
            &nbsp;&nbsp;&nbsp;.
          </Cond>
          {options.label}
        </label>
        <div class="col-sm-8">
          <div class="input-group">
            <div class="input-group-btn">
              <button
                type="button"
                class="btn btn-include"
                class:btn-primary={options.optional}
                class:required={!options.optional}
                class:toggled={options.optional && options.include}
                tabindex={options.optional ? '0' : '-1'}
                onclick={options.optional ? this._onIncludeClick : null}
              />
            </div>

            <FormInputElement inputID={inputID} options={options} ref={el} />

            <Cond test={options.type === 'array'}>
              <div class="input-group-btn array-controls">
                <FormArrayButton type="add" paramName={fieldName} />
              </div>
            </Cond>
          </div>
        </div>
      </div>
    );

    if (form) {
      form.addField(fieldName, {
        root,
        el,
        label: options.label,
        getValue() {
          return el.value;
        },
        setValue(val) {
          el.value = val;
        },
      });
    }

    return root;
  }

  isParamIncluded(input) {
    const btn = input
      .closest('.form-group')
      .querySelector('.btn-include:not(.required)');

    if (btn && !btn.classList.contains('toggled')) {
      return 0;
    }

    return 1;
  }

  // TODO this is messed up. just tabbing through fields marks them
  // as visited and then tabbing through again selects them
  // (e.g. doing shift tab to go backwards selects all previous fields
  // that you tabbed through)
  //
  // maybe we should ignore whether a field was 'visited' or not and
  // instead just include it if the input is non-empty?
  _formEventHandler(e) {
    const { target } = e;
    if (!target.matches('input, select')) {
      return;
    }

    if (e.type === 'keyup' && target.visited) {
      const btn = target
        .closest('.form-group')
        .querySelector('.btn-include:not(.required)');

      if (btn) {
        btn.classList.add('toggled');
      }
    }

    target.visited = true;

    this._generateRequestDebounced();
  }

  // TODO make this a delegated event again
  _onIncludeClick(e) {
    e.target.classList.toggle('toggled');

    this._generateRequestDebounced();
  }

  setAuthType() {
    const type = this._requestForm.get('authType');

    Object.entries(RequestAuth.getMetadata().authTypes).forEach(([ authType, authTypeOpts ]) => {
      const isVisible = authType === type;
      authTypeOpts.params.forEach(authParamName => {
        this._authForm.setVisibility(`${authType}_${authParamName}`, isVisible);
      });
    });

    if (initialized)
      this.setRequestType();
  }

  getAuthData(authType) {
    const authMetadata = RequestAuth.getMetadata();
    const authTypes = authMetadata.authTypes;
    const authFormData = this._authForm.getAllValues();

    const { params } = authTypes[authType];

    var ret = {
      type : authType
    };

    params.forEach(field => {
      const fullFieldName = `${authType}_${field}`;
      if (!this.isParamIncluded(this._authForm.getInput(fullFieldName))) {
        return;
      }

      ret[field] = authFormData[fullFieldName];
    });

    if (this._siteConfig.SAVE_AUTH_FIELDS)
      localStorage.apiAuth = JSON.stringify(authFormData);

    return ret;
  }

  getRequestData() {
    const ret = {
      ...this._requestForm.getAllValues(),
      params: {},
      includeParam: {},
    };

    this._getParamInputs().forEach(input => {
      const id = input.id.substr(2);

      if (input.tagName === 'DIV') { // array/object
        const isObject = input.hasAttribute('data-is-object');

        if (isObject) {
          ret.params[id] = {};
        } else {
          ret.params[id] = [];
          document.querySelectorAll(`[data-name="${id}"] input`).forEach(arrayInput => {
            ret.params[id].push(arrayInput.value);
          });
        }
      } else {
        ret.params[id] = input.value;
      }

      ret.includeParam[id] = this.isParamIncluded(input);
    });

    return ret;
  }

  getDestinationURL(reqData) {
    return reqData.requestURL +
      '/' + reqData.apiVersion + '/' + reqData.requestGroup;
  }

  _arrayAction(e) {
    const btn = e.target.closest('button[data-array-action-type]');
    if (!btn) {
      return;
    }

    const action = btn.getAttribute('data-array-action-type');
    const paramName = btn.getAttribute('data-array-action-param');
    const parent = btn.closest('.form-group');

    switch (action) {
    case 'add':
      parent.after(
        <FormArrayElement paramName={paramName} />
      );

      break;

    case 'remove':
      parent.remove();

      break;
    }
  }

  generateRequest() {
    var reqData    = this.getRequestData(),
        editorMode = 'ace/mode/text',
        authData   = this.getAuthData(reqData.authType),
        url        = this.getDestinationURL(reqData);

    const { requestFormat } = reqData;

    document.querySelector('.destination-url').textContent = `${window.location.protocol}${url}`;

    var envelopeOptions = {
      outputFormat : reqData.outputFormat
    };

    if (reqData.errorURL) {
      envelopeOptions.errorURL = reqData.errorURL;
    }

    var envelope = new RequestEnvelope(reqData.requestGroup, new RequestAuth(authData), envelopeOptions);

    if (reqData.requestType) {
      var requestParams = ApiMetadata.getMethodDefinition(reqData.requestGroup, reqData.requestType).parameters;

      var params = this.getObjectData(reqData, requestParams);

      envelope.addSubRequest(reqData.requestType, params, reqData.requestID);
    }

    var text = RequestSerializer.serialize(requestFormat, envelope);

    if (requestFormat === 'xml') {
      text = formatXml(text);
      editorMode = 'ace/mode/xml';
    } else if (requestFormat === 'json') {
      editorMode = 'ace/mode/json';
    }


    this._requestEditor.getSession().setMode(editorMode);
    this._requestEditor.setValue(text, -1);
  }

  getObjectData(reqData, def, prefix = '') {
    var ret = {};

    for (var paramName in def) {
      var opts = def[paramName];
      var fullParamName = `${prefix}${paramName}`;

      if (opts.required || (opts.requiredForAuthType && this.authTypeMatches(reqData.authType, opts.requiredForAuthType)) || reqData.includeParam[fullParamName]) {
        let val;
        if (opts.type === 'object' && opts.objectType) {
          val = this.getObjectData(reqData, opts.fields, `${paramName}.`);
        } else {
          val = reqData.params[fullParamName];

          if (opts.nullable && val === '') {
            val = null;
          } else if (opts.type === 'integer' || opts.type === 'boolean') {
            val = parseInt(val);
          }

          if (opts.type === 'boolean' && val !== null) {
            val = !!val;
          }
        }

        ret[paramName] = val;
      }
    }

    return ret;
  }

  sendRequest() {
    document.querySelector('.requestStatus').textContent = 'Sending Request...';

    const formData = this._requestForm.getAllValues();

    var requestContentType = null;
    let body = this._requestEditor.getValue();
    var method = 'POST';

    var url = this.getDestinationURL(formData);

    if (formData.requestFormat === 'get' || formData.requestFormat === 'post') {
      body = body.replace(/[\r\n]/g, '');
    }

    switch (formData.requestFormat) {
    case 'xml':
      requestContentType = 'text/xml';
      break;

    case 'json':
      requestContentType = 'application/json';
      break;

    case 'get':
      method = 'GET';
      url += `?${body}`;
      break;

    case 'post':
      requestContentType = 'application/x-www-form-urlencoded';
      break;
    }

    const options = {
      method,
    };

    if (method !== 'GET') {
      options.body = body;
    }

    if (requestContentType) {
      options.headers = {
        'Content-Type': requestContentType,
      };
    }

    let resp = null;

    fetch(url, options)
      .then(response => {
        resp = response;

        this.updateTime();

        return resp.text();
      })
      .then(data => {
        var editorMode = 'ace/mode/text';

        if (!resp.ok) {
          throw new Error(`Received error status response: ${resp.status}\n\nResponse Body:\n\n${data}`);
        }

        const contentType = resp.headers.get('content-type')?.toLowerCase();

        if (contentType.indexOf('text/xml') === 0) {
          editorMode = 'ace/mode/xml';

          data = formatXml(data);
        } else if (contentType.indexOf('application/json') === 0) {
          try {
            data = JSON.parse(data);

            // only set mode if parse is successful
            editorMode = 'ace/mode/json';

            data = JSON.stringify(data, null, 2);
          } catch (exception) {
            //console.log(exception);
            data = 'Error parsing json: \n\n' + data;
          }
        } else if (contentType.indexOf('text/csv') === 0) {
          // TODO
        }

        this._responseEditor.getSession().setMode(editorMode);
        this._responseEditor.setValue(data, -1);
      })
      .catch(err => {
        this.updateTime();

        this._responseEditor.getSession().setMode('ace/mode/text');

        this._responseEditor.setValue(err.message, -1);
      });

    this._sentTime = Date.now();
  }

  activate() {
    this._layoutManager.update();
  }

  _getParamInputs() {
    return document.querySelectorAll('.dynamic-params .form-group .paramValue');
  }
}

function FormInputElement({ inputID, options, ref }) {
  let el;

  const { type } = options;
  if (type === 'enum' || type === 'boolean') {
    el = (
      <select id={inputID} class="form-control paramValue" />
    );

    if (options.nullable) {
      el.appendChild(new Option('null', ''));
    }

    if (type === 'boolean') {
      el.appendChild(new Option('false', '0'));
      el.appendChild(new Option('true', '1'));
    } else {
      for (const opt of options.values) {
        el.appendChild(new Option(opt, opt));
      }
    }

    if ('paramValue' in options) {
      el.value = options.paramValue;
    }
  } else if (type === 'array') {
    el = (
      <div
        id={inputID}
        class="form-control paramValue"
      >
        Array
      </div>
    );
  } else if (type === 'object') {
    el = (
      <div
        id={inputID}
        class="form-control paramValue"
        data-is-object="1"
      >
        Object
      </div>
    );
  } else {
    el = (
      <input id={inputID} class="form-control paramValue" />
    );

    if ('paramValue' in options) {
      el.value = options.paramValue;
    }

    // TODO this is kind of a hack.
    // - its only used for authForm fields
    // - technically ref should also apply to other input types returned from this function
    if (ref) {
      ref(el);
    }
  }

  return el;
}

function FormArrayElement({ paramName }) {
  return (
    <div class="form-group" data-name={paramName}>
      <label class="col-sm-4 control-label">&nbsp;</label>
      <div class="col-sm-8">
        <div class="input-group">
          <input type="text" class="form-control" />
          <div class="input-group-btn array-controls">
            <FormArrayButton type="add" paramName={paramName} />
            <FormArrayButton type="remove" paramName={paramName} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FormArrayButton({ type, paramName }) {
  return (
    <button
      type="button"
      class="btn btn-primary"
      data-array-action-type={type}
      data-array-action-param={paramName}
    >
      <span
        class:tbicon-plus={type === 'add'}
        class:tbicon-cross={type === 'remove'}
      />
    </button>
  );
}
